import React, { useEffect, useState, useCallback } from 'react';

/**
 * Product configuration for the paywall display.
 */
export const EF_PRODUCTS = {
  'ef-strategy': {
    slug: 'ef-strategy',
    displayName: 'EF-Strategy',
    priceGBP: 99,
    description: 'AI-powered business strategy and executive intelligence',
    features: [
      'AI Strategy Advisor with multi-agent workflows',
      '300 strategic AI queries per month',
      'Business simulations & scenario planning',
      'Executive briefings & board reports',
      'Market research & competitor analysis',
      'Document intelligence & knowledge base',
      'Priority support',
    ],
  },
  'ef-crm': {
    slug: 'ef-crm',
    displayName: 'EF-CRM',
    priceGBP: 29,
    description: 'AI-powered customer relationship management',
    features: [
      'AI Sales Assistant & deal insights',
      '500 AI actions per month',
      'Pipeline forecasting & analytics',
      'Automated follow-up suggestions',
      'Lead scoring & qualification',
      'Email intelligence',
      'Standard support',
    ],
  },
  'ef-capflow': {
    slug: 'ef-capflow',
    displayName: 'EF-CapFlow',
    priceGBP: 29,
    description: 'AI-powered financial management & accounting',
    features: [
      'AI CFO Agent & financial insights',
      '500 AI queries per month',
      'Cash flow forecasting',
      'HMRC MTD integration',
      'Expense tracking & reporting',
      'Invoice management',
      'Standard support',
    ],
  },
  'ef-link': {
    slug: 'ef-link',
    displayName: 'EF-Link',
    priceGBP: 29,
    description: 'AI-powered supply chain intelligence',
    features: [
      'AI Supply Chain Advisor',
      '500 AI actions per month',
      'Supplier risk assessment',
      'Demand forecasting',
      'Inventory optimization',
      'Disruption alerts',
      'Standard support',
    ],
  },
  'ef-projex': {
    slug: 'ef-projex',
    displayName: 'EF-Projex',
    priceGBP: 29,
    description: 'AI-powered project management',
    features: [
      'AI Project Manager Agent',
      '500 AI actions per month',
      'Risk prediction & mitigation',
      'Resource optimization',
      'Status report generation',
      'Milestone tracking',
      'Standard support',
    ],
  },
};

/**
 * SubscriptionPaywall — wraps protected content and shows a paywall
 * if the user doesn't have an active subscription.
 * 
 * Tailwind CSS version for EFAIPM and similar JSX apps.
 */
export default function SubscriptionPaywall({
  product,
  apiBaseUrl = '',
  children,
  getAuthToken,
  userId,
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = { 'Content-Type': 'application/json' };

      if (getAuthToken) {
        const token = await getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      if (userId) headers['X-User-ID'] = userId;

      const response = await fetch(
        `${apiBaseUrl}/api/v1/billing/subscription/status?product=${product.slug}`,
        { headers, credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      } else {
        setStatus({ has_subscription: true, status: 'active', reason: 'endpoint_unavailable' });
      }
    } catch (err) {
      console.error('Subscription check failed:', err);
      setStatus({ has_subscription: true, status: 'active', reason: 'check_failed' });
    } finally {
      setLoading(false);
    }
  }, [product.slug, apiBaseUrl, getAuthToken, userId]);

  useEffect(() => { checkSubscription(); }, [checkSubscription]);

  // Re-check after returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_id')) {
      checkSubscription();
      const url = new URL(window.location.href);
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
    }
  }, [checkSubscription]);

  const handleCheckout = async () => {
    try {
      setCheckoutLoading(true);
      const headers = { 'Content-Type': 'application/json' };
      if (getAuthToken) {
        const token = await getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      if (userId) headers['X-User-ID'] = userId;

      const response = await fetch(`${apiBaseUrl}/api/v1/billing/checkout`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          product: product.slug,
          success_url: window.location.origin + '/dashboard?session_id={CHECKOUT_SESSION_ID}',
          cancel_url: window.location.href,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        setError('Failed to start checkout. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect to billing service.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setCheckoutLoading(true);
      const headers = { 'Content-Type': 'application/json' };
      if (getAuthToken) {
        const token = await getAuthToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      if (userId) headers['X-User-ID'] = userId;

      const response = await fetch(`${apiBaseUrl}/api/v1/billing/portal`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ return_url: window.location.href }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      } else {
        setError('Failed to open billing portal.');
      }
    } catch (err) {
      setError('Failed to connect to billing service.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <p className="text-gray-500">Checking subscription...</p>
      </div>
    );
  }

  // User has active subscription or trial — render children
  if (status?.has_subscription) {
    return <>{children}</>;
  }

  // ── PAYWALL ────────────────────────────────────────────────────────────
  const isPastDue = status?.status === 'past_due';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center py-8 px-4">
      <div className="max-w-md w-full bg-slate-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-indigo-500/30 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-8 px-6 text-center">
          <div className="text-5xl mb-3">
            {isPastDue ? '💳' : '🚀'}
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {isPastDue ? 'Payment Required' : 'Start Your Free Trial'}
          </h1>
          <p className="text-white/85">
            {isPastDue
              ? 'Your payment is overdue. Please update your payment method.'
              : `Try ${product.displayName} free for 7 days`}
          </p>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300 ml-2">✕</button>
            </div>
          )}

          {!isPastDue && (
            <>
              {/* Pricing */}
              <div className="text-center mb-6">
                <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 text-sm font-bold px-3 py-1 rounded-full mb-3">
                  ⭐ 7-DAY FREE TRIAL
                </span>
                <div className="text-4xl font-bold text-white">
                  £{product.priceGBP}
                  <span className="text-lg text-gray-400 ml-1">/month</span>
                </div>
                <p className="text-gray-400 text-sm mt-1">
                  No charge for 7 days. Cancel anytime.
                </p>
              </div>

              <hr className="border-white/10 my-4" />

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-300 text-sm">
                    <span className="text-green-400 text-lg">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* CTA Button */}
          <button
            onClick={isPastDue ? handleManageBilling : handleCheckout}
            disabled={checkoutLoading}
            className={`w-full py-3 px-4 rounded-xl font-bold text-lg text-white transition-all duration-200 flex items-center justify-center gap-2 ${
              checkoutLoading
                ? 'opacity-50 cursor-not-allowed'
                : isPastDue
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
            }`}
          >
            {checkoutLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Redirecting...
              </>
            ) : isPastDue ? (
              <>💳 Update Payment Method</>
            ) : (
              <>🔒 Start Free Trial</>
            )}
          </button>

          <p className="text-center text-gray-500 text-xs mt-3">
            {isPastDue
              ? "You'll be redirected to Stripe to update your payment details."
              : '🔒 Secure checkout powered by Stripe. Cancel anytime during your trial.'}
          </p>
        </div>
      </div>
    </div>
  );
}
